/**
 * RequestDetails — Request Lock mixin.
 *
 * ROI Specialist: hard lock — all interactive elements disabled while another user holds it.
 * Admin:          soft lock — informational banner only, can still edit.
 */

const LOCK_HEARTBEAT_MS = 30_000; // 30 s
const LOCK_BANNER_ID    = 'ds-request-lock-banner';

export const LockMixin = {

    // ── Internal state ────────────────────────────────────────────────────────

    _lockHeartbeatTimer: null,
    _lockRequestId: null,
    _lockActive: false,   // true = current user holds the lock
    _lockLocked: false,   // true = someone else holds the lock (we're viewing in locked state)

    // ── Public API ────────────────────────────────────────────────────────────

    /**
     * Called after fetchDetails resolves. Tries to acquire the lock.
     */
    acquireLock( requestId ) {
        this._lockRequestId = String( requestId );
        this._lockActive    = false;
        this._lockLocked    = false;

        // Locking only applies between admins/ROI specialists. Case managers / company
        // users never acquire a lock and never see the "Request in Use" overlay.
        if ( ! this._isLockEligibleRole() ) return;

        const formData = new URLSearchParams();
        formData.append( 'action', 'docuswift_acquire_request_lock' );
        formData.append( 'request_id', this._lockRequestId );
        formData.append( 'nonce', window.docuswiftPortal?.nonce || '' );

        fetch( this.ajaxUrl, {
            method:  'POST',
            headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
            body:    formData.toString(),
        } )
            .then( r => r.json() )
            .then( res => {
                if ( ! res?.success ) return;
                const data = res.data;

                if ( data.locked ) {
                    // Admin-vs-admin: skip the overlay entirely, open normally.
                    const currentIsAdmin = ! this._isHardLockRole() &&
                        ( window.docuswiftPortal?.current_user_roles || [] ).includes( 'administrator' );
                    if ( currentIsAdmin && data.lock_user_is_admin ) {
                        this._lockActive = false;
                        this._lockLocked = false;
                        return;
                    }
                    // Someone else holds the lock — show overlay.
                    this._lockLocked = true;
                    this._applyLockUI( data.lock_user_name );
                } else {
                    // We acquired / refreshed the lock.
                    this._lockActive = true;
                    this._removeLockUI();
                    this._startLockHeartbeat();
                }
            } )
            .catch( () => {} );
    },

    /**
     * Release the lock and stop heartbeat. Called on panel close.
     */
    releaseLock() {
        this._stopLockHeartbeat();
        this._removeLockUI();

        if ( ! this._lockActive || ! this._lockRequestId ) return;

        this._lockActive = false;

        const formData = new URLSearchParams();
        formData.append( 'action', 'docuswift_release_request_lock' );
        formData.append( 'request_id', this._lockRequestId );
        formData.append( 'nonce', window.docuswiftPortal?.nonce || '' );

        // Fire-and-forget — use sendBeacon so it survives page unload.
        if ( navigator.sendBeacon ) {
            navigator.sendBeacon( this.ajaxUrl, formData );
        } else {
            fetch( this.ajaxUrl, {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    formData.toString(),
                keepalive: true,
            } ).catch( () => {} );
        }
    },

    // ── Private ───────────────────────────────────────────────────────────────

    _startLockHeartbeat() {
        this._stopLockHeartbeat();
        this._lockHeartbeatTimer = setInterval( () => {
            if ( ! this._lockActive || ! this._lockRequestId ) return;
            const formData = new URLSearchParams();
            formData.append( 'action', 'docuswift_heartbeat_request_lock' );
            formData.append( 'request_id', this._lockRequestId );
            formData.append( 'nonce', window.docuswiftPortal?.nonce || '' );
            fetch( this.ajaxUrl, {
                method:  'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body:    formData.toString(),
            } ).catch( () => {} );
        }, LOCK_HEARTBEAT_MS );
    },

    _stopLockHeartbeat() {
        if ( this._lockHeartbeatTimer ) {
            clearInterval( this._lockHeartbeatTimer );
            this._lockHeartbeatTimer = null;
        }
    },

    /**
     * Determine if the current user is a hard-lock target (ROI Specialist).
     * Admins get soft lock (banner only).
     */
    _isHardLockRole() {
        const roles = window.docuswiftPortal?.current_user_roles || [];
        return roles.includes( 'roi_specialist' ) && ! roles.includes( 'administrator' );
    },

    /**
     * Only admins/ROI specialists participate in the lock (acquire, get blocked,
     * or see the "Request in Use" / "Request Locked" overlay).
     */
    _isLockEligibleRole() {
        const roles = window.docuswiftPortal?.current_user_roles || [];
        return roles.includes( 'administrator' ) || roles.includes( 'roi_specialist' );
    },

    _applyLockUI( lockerName ) {
        if ( ! this.details ) return;

        this._removeLockUI();

        const isHard  = this._isHardLockRole();
        const overlay = document.createElement( 'div' );
        overlay.id        = LOCK_BANNER_ID;
        overlay.className = 'ds-request-lock-overlay' + ( isHard ? ' ds-request-lock-overlay--hard' : ' ds-request-lock-overlay--soft' );

        const iconLock = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>`;
        const iconEye  = `<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>`;

        const buttonsHtml = isHard
            ? `<div class="ds-request-lock-overlay__buttons">
                   <button type="button" class="button white ds-lock-close-btn">Close Request</button>
               </div>`
            : `<div class="ds-request-lock-overlay__buttons">
                   <button type="button" class="button white ds-lock-close-btn">Close Request</button>
                   <button type="button" class="button gold ds-lock-continue-btn">Continue</button>
               </div>`;

        overlay.innerHTML = `
            <div class="ds-request-lock-overlay__card">
                <div class="ds-request-lock-overlay__icon">${ isHard ? iconLock : iconEye }</div>
                <h3 class="ds-request-lock-overlay__title">${ isHard ? 'Request Locked' : 'Request in Use' }</h3>
                <p class="ds-request-lock-overlay__desc">
                    ${ isHard
                        ? `<strong>${ lockerName }</strong> is currently editing this request. Editing is disabled until they close it.`
                        : `<strong>${ lockerName }</strong> is currently viewing this request.`
                    }
                </p>
                ${ buttonsHtml }
            </div>`;

        // Close button — skip follow-up reminder since user didn't actually edit anything.
        overlay.querySelector( '.ds-lock-close-btn' )?.addEventListener( 'click', () => {
            this._skipFollowUpCheck = true;
            this.hide();
        } );

        // Continue button (soft lock only) — dismiss overlay, allow editing.
        overlay.querySelector( '.ds-lock-continue-btn' )?.addEventListener( 'click', () => {
            this._removeLockUI();
        } );

        this.details.appendChild( overlay );
        this.details.style.overflow = 'hidden';
    },

    _removeLockUI() {
        document.getElementById( LOCK_BANNER_ID )?.remove();
        if ( this.details ) this.details.style.overflow = '';
        this._enableRequestPanel();
    },

    _disableRequestPanel() {
        if ( ! this.details ) return;
        const selectors = 'button:not(.close_request_detail):not(#' + LOCK_BANNER_ID + ' *), input, textarea, select, a.button';
        this.details.querySelectorAll( selectors ).forEach( el => {
            if ( el.closest( '#' + LOCK_BANNER_ID ) ) return;
            el.setAttribute( 'data-ds-lock-disabled', '1' );
            if ( el.tagName === 'A' ) {
                el.setAttribute( 'data-ds-lock-href', el.href || '' );
                el.removeAttribute( 'href' );
                el.style.pointerEvents = 'none';
                el.style.opacity       = '0.5';
            } else {
                el.disabled = true;
            }
        } );
        // Disable NiceSelect custom dropdowns.
        this.details.querySelectorAll( '.nice-select' ).forEach( el => {
            if ( el.closest( '#' + LOCK_BANNER_ID ) ) return;
            el.setAttribute( 'data-ds-lock-disabled', '1' );
            el.classList.add( 'disabled' );
            el.style.pointerEvents = 'none';
            el.style.opacity       = '0.5';
        } );
    },

    _enableRequestPanel() {
        if ( ! this.details ) return;
        this.details.querySelectorAll( '[data-ds-lock-disabled]' ).forEach( el => {
            el.removeAttribute( 'data-ds-lock-disabled' );
            if ( el.tagName === 'A' ) {
                const href = el.getAttribute( 'data-ds-lock-href' );
                if ( href ) el.setAttribute( 'href', href );
                el.removeAttribute( 'data-ds-lock-href' );
                el.style.pointerEvents = '';
                el.style.opacity       = '';
            } else {
                el.disabled = false;
            }
            // NiceSelect
            if ( el.classList.contains( 'nice-select' ) ) {
                el.classList.remove( 'disabled' );
                el.style.pointerEvents = '';
                el.style.opacity       = '';
            }
        } );
    },
};
